import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import PageWrapper from "../components/PageWrapper";
import { FadeUp } from "../components/motion";
import { submitEnquiry, submitCatalogRequest, fetchCategories } from "../lib/api";
import { toast, Toaster } from "sonner";

const ENQUIRY_TYPES = [
  { v: "general", l: "General Enquiry" },
  { v: "product", l: "Specific Product" },
  { v: "hospitality", l: "Hospitality / Resorts" },
  { v: "catalog", l: "Request Catalog" },
  { v: "partnership", l: "Partnerships" },
];

const SEGMENTS = ["B2C — I'm a homeowner / landowner", "B2B — I'm a developer / operator"];

export default function Contact() {
  const [params] = useSearchParams();
  const initialType = params.get("enquiry_type") || "general";

  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    enquiry_type: initialType,
    segment: "",
    name: "",
    email: "",
    phone: "",
    company: "",
    location: "",
    product_slug: "",
    budget: "",
    timeline: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  const handle = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      const segment = form.segment.startsWith("B2C") ? "B2C" : form.segment.startsWith("B2B") ? "B2B" : null;
      if (form.enquiry_type === "catalog") {
        await submitCatalogRequest({
          name: form.name,
          email: form.email,
          phone: form.phone,
          interest: form.message,
        });
        toast.success("Catalog request received. We'll send the PDF shortly.");
      } else {
        await submitEnquiry({ ...form, segment });
        toast.success("Enquiry received. Our team will reach out within 24 hours.");
      }
      setForm({ ...form, name: "", email: "", phone: "", company: "", location: "", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <Toaster position="top-center" richColors />

      {/* Header */}
      <section className="pt-[150px] pb-12" data-testid="contact-header">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14">
          <FadeUp>
            <div className="eyebrow eyebrow-dash text-[#D9A441] mb-5">Contact</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F2EDE3]">
              Start your <span className="italic text-[#D9A441]">project.</span>
            </h1>
            <p className="mt-5 text-sm md:text-base text-[#F2EDE3]/60 max-w-xl leading-relaxed">
              Tell us about your land, your brief or your brand. Our team responds within 24 hours.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Form + Info */}
      <section className="pb-24">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Sidebar info */}
          <aside className="lg:col-span-4 order-2 lg:order-1 space-y-5">
            <FadeUp className="panel p-8">
              <div className="eyebrow text-[#D9A441] mb-5">Direct Channels</div>
              <div className="space-y-4">
                <a href="mailto:business@nuvunhomes.com" data-testid="contact-email-link" className="flex items-start gap-3 text-sm text-[#F2EDE3]/75 hover:text-[#D9A441] transition-colors">
                  <Mail className="w-4 h-4 mt-0.5 text-[#D9A441] shrink-0" /> business@nuvunhomes.com
                </a>
                <a href="mailto:info@nuvunhomes.com" data-testid="contact-email-link-2" className="flex items-start gap-3 text-sm text-[#F2EDE3]/75 hover:text-[#D9A441] transition-colors">
                  <Mail className="w-4 h-4 mt-0.5 text-[#D9A441] shrink-0" /> info@nuvunhomes.com
                </a>
                <a href="tel:+919696960998" data-testid="contact-phone-link" className="flex items-start gap-3 text-sm text-[#F2EDE3]/75 hover:text-[#D9A441] transition-colors">
                  <Phone className="w-4 h-4 mt-0.5 text-[#D9A441] shrink-0" /> +91 96969 60998
                </a>
                <div className="flex items-start gap-3 text-sm text-[#F2EDE3]/55 leading-relaxed">
                  <MapPin className="w-4 h-4 mt-0.5 text-[#D9A441] shrink-0" />
                  <span>Brillen Space Engineering, 5th Floor,<br />Eashan Arcade, Doctor&apos;s Colony,<br />Madhapur, Hyderabad, Telangana 500081</span>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.08} className="panel p-8">
              <div className="eyebrow text-[#F2EDE3]/50 mb-3">Response Time</div>
              <div className="font-condensed text-4xl text-[#D9A441] mb-2">&lt; 24 hrs</div>
              <div className="text-sm text-[#F2EDE3]/55 leading-relaxed">
                Every enquiry is read by the NUVUN platform team — no chatbots, no auto-replies.
              </div>
            </FadeUp>

            <FadeUp delay={0.14} className="panel p-8 !border-[#D9A441]/45">
              <div className="eyebrow text-[#D9A441] mb-3">Browse first?</div>
              <div className="font-display text-xl text-[#F2EDE3] leading-tight mb-4">
                Explore 39 models across 6 collections.
              </div>
              <Link to="/products" data-testid="contact-browse-products" className="inline-flex items-center gap-2 eyebrow text-[#D9A441] hover:text-[#EEBF04] transition-colors">
                Open Catalogue <ArrowUpRight className="w-4 h-4" />
              </Link>
            </FadeUp>
          </aside>

          {/* Form */}
          <FadeUp delay={0.05} className="lg:col-span-8 order-1 lg:order-2">
            <form onSubmit={submit} data-testid="contact-form" className="panel p-6 md:p-10">
              {/* Enquiry type */}
              <div className="mb-8">
                <div className="eyebrow text-[#F2EDE3]/50 mb-3">I&apos;m here for</div>
                <div className="flex flex-wrap gap-2" data-testid="enquiry-type-tabs">
                  {ENQUIRY_TYPES.map((t) => (
                    <button
                      type="button"
                      key={t.v}
                      onClick={() => setForm((f) => ({ ...f, enquiry_type: t.v }))}
                      data-testid={`enquiry-type-${t.v}`}
                      className={`eyebrow px-4 py-2.5 rounded-sm border transition-colors ${
                        form.enquiry_type === t.v
                          ? "border-[#D9A441] bg-[#D9A441]/10 text-[#D9A441]"
                          : "border-white/15 text-[#F2EDE3]/60 hover:border-white/40"
                      }`}
                    >
                      {t.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Segment */}
              <div className="mb-8">
                <div className="eyebrow text-[#F2EDE3]/50 mb-3">Who are you?</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="segment-selector">
                  {SEGMENTS.map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => setForm((f) => ({ ...f, segment: s }))}
                      data-testid={`segment-${s.startsWith("B2C") ? "b2c" : "b2b"}`}
                      className={`text-left p-4 rounded-sm border transition-colors ${
                        form.segment === s
                          ? "border-[#D9A441] bg-[#D9A441]/[0.07]"
                          : "border-white/15 hover:border-white/40"
                      }`}
                    >
                      <div className="font-display text-lg text-[#F2EDE3]">{s.split("—")[0].trim()}</div>
                      <div className="text-xs text-[#F2EDE3]/50 mt-1">{s.split("—")[1]?.trim()}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input data-testid="contact-name" required value={form.name} onChange={handle("name")} placeholder="Full name *" className="field" />
                <input data-testid="contact-email" required type="email" value={form.email} onChange={handle("email")} placeholder="Email *" className="field" />
                <input data-testid="contact-phone-input" value={form.phone} onChange={handle("phone")} placeholder="Phone" className="field" />
                <input data-testid="contact-company" value={form.company} onChange={handle("company")} placeholder="Company / Project name" className="field" />
                <input data-testid="contact-location" value={form.location} onChange={handle("location")} placeholder="Site location (city / state)" className="field" />
                <select data-testid="contact-product" value={form.product_slug} onChange={handle("product_slug")} className="field">
                  <option value="">Interested in (optional)</option>
                  {categories.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.name}</option>
                  ))}
                </select>
                <select data-testid="contact-budget" value={form.budget} onChange={handle("budget")} className="field">
                  <option value="">Budget range (optional)</option>
                  <option>Under ₹25 L</option>
                  <option>₹25 L - ₹50 L</option>
                  <option>₹50 L - ₹1 Cr</option>
                  <option>₹1 Cr - ₹5 Cr</option>
                  <option>₹5 Cr+</option>
                </select>
                <select data-testid="contact-timeline" value={form.timeline} onChange={handle("timeline")} className="field">
                  <option value="">Timeline</option>
                  <option>Immediate (within 60 days)</option>
                  <option>1-3 months</option>
                  <option>3-6 months</option>
                  <option>Exploring</option>
                </select>
                <textarea data-testid="contact-message" required value={form.message} onChange={handle("message")} rows={5} placeholder="Tell us about your site, your brief or your brand *" className="field sm:col-span-2" />
              </div>

              <button type="submit" data-testid="contact-submit" disabled={submitting} className="btn-gold mt-6 w-full sm:w-auto disabled:opacity-60">
                {submitting ? "Submitting…" : "Send Enquiry"} <ArrowUpRight className="w-4 h-4" />
              </button>
              <p className="mt-3 text-[11px] text-[#F2EDE3]/40">
                By submitting, you agree to be contacted by the NUVUN team about your enquiry.
              </p>
            </form>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
}
